import { useState, useCallback } from 'react';

export type RecommendationStatus = 'pending' | 'in_progress' | 'fixed' | 'wont_fix';

export interface RecommendationTracking {
  id: string;
  status: RecommendationStatus;
  assignee: string | null;
  notes: string | null;
  updatedAt: string;
}

interface TrackingStore {
  [siteId: string]: {
    [recId: string]: RecommendationTracking;
  };
}

const STORAGE_KEY = 'audit-recommendation-tracking';

function loadTrackingData(): TrackingStore {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

function saveTrackingData(data: TrackingStore): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    console.error('Failed to save tracking data:', e);
  }
}

export function useRecommendationTracking(siteId: string) {
  const [trackingData, setTrackingData] = useState<TrackingStore>(() => loadTrackingData());

  // Get tracking for a specific recommendation
  const getTracking = useCallback((recId: string): RecommendationTracking | null => {
    return trackingData[siteId]?.[recId] || null;
  }, [trackingData, siteId]);

  // Update tracking for a recommendation
  const updateTracking = useCallback((
    recId: string,
    updates: Partial<Omit<RecommendationTracking, 'id' | 'updatedAt'>>
  ) => {
    setTrackingData(prev => {
      const newData = { ...prev };
      if (!newData[siteId]) {
        newData[siteId] = {};
      }

      const existing = newData[siteId][recId] || {
        id: recId,
        status: 'pending',
        assignee: null,
        notes: null,
        updatedAt: new Date().toISOString(),
      };

      newData[siteId][recId] = {
        ...existing,
        ...updates,
        updatedAt: new Date().toISOString(),
      };

      saveTrackingData(newData);
      return newData;
    });
  }, [siteId]);

  // Get all tracked recommendations for the site
  const getSiteTracking = useCallback((): Record<string, RecommendationTracking> => {
    return trackingData[siteId] || {};
  }, [trackingData, siteId]);

  // Get stats for the site
  const getStats = useCallback(() => {
    const siteData = trackingData[siteId] || {};
    const entries = Object.values(siteData);

    return {
      total: entries.length,
      pending: entries.filter(t => t.status === 'pending').length,
      inProgress: entries.filter(t => t.status === 'in_progress').length,
      fixed: entries.filter(t => t.status === 'fixed').length,
      wontFix: entries.filter(t => t.status === 'wont_fix').length,
    };
  }, [trackingData, siteId]);

  // Clear all tracking for the site
  const clearSiteTracking = useCallback(() => {
    setTrackingData(prev => {
      const newData = { ...prev };
      delete newData[siteId];
      saveTrackingData(newData);
      return newData;
    });
  }, [siteId]);

  return {
    getTracking,
    updateTracking,
    getSiteTracking,
    getStats,
    clearSiteTracking,
  };
}

// Hook for global stats across all sites
export function useAllSitesTrackingStats() {
  const [trackingData] = useState<TrackingStore>(() => loadTrackingData());

  const getGlobalStats = useCallback(() => {
    const allEntries = Object.values(trackingData).flatMap(site => Object.values(site));

    return {
      total: allEntries.length,
      pending: allEntries.filter(t => t.status === 'pending').length,
      inProgress: allEntries.filter(t => t.status === 'in_progress').length,
      fixed: allEntries.filter(t => t.status === 'fixed').length,
      wontFix: allEntries.filter(t => t.status === 'wont_fix').length,
    };
  }, [trackingData]);

  return { getGlobalStats };
}
